/**
 * MooredBoatsBuilder.ts — spawns "neighbour" boats sitting in the
 * slips of the annotation-derived docks, wires their mooring lines
 * to real {@link MooringAnchor} objects (dock bollards + buoys) and
 * runs the per-frame slip-pose pin so wind / waves can't drift them
 * out of the marina.
 *
 * Lives in three phases because the dependencies arrive at different
 * times during {@link startApp}:
 *  1. {@link spawn} pushes {@link Boat} instances into the shared
 *     `boats[]` array BEFORE the render passes are constructed
 *     (those passes snapshot `boats.length` at build time).
 *  2. {@link wireMooringLines} runs AFTER docks + buoys exist;
 *     each slot resolves to two stern lines (nearest dock-edge
 *     bollards) plus an optional bow line to the nearest buoy on
 *     buoy-style docks.
 *  3. {@link stepAndPin} + {@link updateRopeGeometry} run inside
 *     the rAF loop. Moored boats integrate (so they bob with
 *     waves) but their XZ pose + heading get clamped to the slip
 *     pose recorded at spawn time.
 *
 * Currently dormant: `MAX_MOORED_BOATS` in {@link startApp} is `0`
 * because the player-mooring story is the focus, but the code path
 * stays here so flipping the cap repopulates the marina without
 * any orchestration changes.
 */
import { Boat } from "./Boat";
import type { BoatLibrary } from "./BoatLibrary";
import type { Buoy } from "./Buoy";
import { dockSpecFromFootprint, type DockFootprint } from "./building-extrude";
import type { Dock, MooringAnchor, MooringStyle } from "./Dock";
import { mooringSlipLayout } from "../assets/ProceduralAssets";
import type { MooringRopeRenderer } from "../rendering/MooringRopeRenderer";
import type { WaterField } from "./WaterField";

interface MooredPose {
  boat: Boat;
  posX: number;
  posZ: number;
  heading: number;
}

interface MooredSlot {
  boat: Boat;
  dockSpecId: string;
  side: -1 | 1;
  sternX: number;
  sternZ: number;
  bowX: number;
  bowZ: number;
  mooringStyle: MooringStyle;
  beam: number;
  loa: number;
}

interface MooredLine {
  boat: Boat;
  cleatBody: [number, number, number];
  anchor: MooringAnchor;
  restLength: number;
  ropeStart: number;
}

/** Stern cleat height above water (m), used for both stern + bow lines. */
const CLEAT_Y_M = 0.75;
/** Stern lines have a small visible sag baked into restLength. */
const STERN_SAG_FACTOR = 1.05;
/** Bow line same — slightly slack so the boat can pitch without snapping. */
const BOW_SAG_FACTOR = 1.05;
/** Floor on rest length so very-near anchors still produce a visible rope. */
const MIN_REST_LENGTH_M = 1.5;
/** Stern sits this far off the dock edge along the slip's outward normal. */
const STERN_STANDOFF_M = 1.0;

export class MooredBoatsBuilder {
  private readonly boats: Boat[];
  private readonly boatLibrary: BoatLibrary;
  private readonly playerBoatId: string;
  private readonly poses: MooredPose[] = [];
  private readonly slots: MooredSlot[] = [];
  private readonly lines: MooredLine[] = [];
  private spawned = 0;
  private ropeRenderer?: MooringRopeRenderer;

  constructor(args: {
    boats: Boat[];
    boatLibrary: BoatLibrary;
    playerBoatId: string;
  }) {
    this.boats = args.boats;
    this.boatLibrary = args.boatLibrary;
    this.playerBoatId = args.playerBoatId;
  }

  /** Total moored boats currently in `boats[]` (always at the tail). */
  get mooredCount(): number {
    return this.spawned;
  }

  /**
   * Spawn up to {@link maxMoored} boats across the supplied dock
   * footprints. Round-robin distribution: every dock gets at least
   * one boat before any one fills up.
   */
  spawn(dockFootprints: readonly DockFootprint[], maxMoored: number): void {
    if (maxMoored <= 0) return;
    const hullIds = this.boatLibrary.ids();
    const slipPlans: Array<{
      footprint: DockFootprint;
      spec: ReturnType<typeof dockSpecFromFootprint>;
      slips: Array<{ side: -1 | 1; xLocal: number; zEdge: number }>;
    }> = [];
    for (let i = 0; i < dockFootprints.length; i++) {
      const f = dockFootprints[i]!;
      if (f.mooringStyle === "dock-only") continue;
      const spec = dockSpecFromFootprint(f, `ann_dock_${i + 1}`);
      const layout = mooringSlipLayout(spec);
      const halfL = spec.length * 0.5;
      const halfW = spec.width * 0.5;
      const slips: Array<{ side: -1 | 1; xLocal: number; zEdge: number }> = [];
      for (let side = 0; side < 2; side++) {
        const zSign = (side === 0 ? -1 : 1) as -1 | 1;
        for (let s = 0; s < layout.slipCount; s++) {
          const t = layout.slipCount === 1 ? 0.5 : (s + 0.5) / layout.slipCount;
          const xLocal = -halfL + t * spec.length;
          // Stern sits a bit off the dock edge; bow points outward.
          const zEdge = zSign * (halfW + STERN_STANDOFF_M);
          slips.push({ side: zSign, xLocal, zEdge });
        }
      }
      slipPlans.push({ footprint: f, spec, slips });
    }
    // Round-robin take one slip from each plan until the cap is hit.
    for (let pass = 0; this.spawned < maxMoored; pass++) {
      let progressed = false;
      for (const plan of slipPlans) {
        if (this.spawned >= maxMoored) break;
        if (pass >= plan.slips.length) continue;
        const slip = plan.slips[pass]!;
        const f = plan.footprint;
        const cosY = Math.cos(f.yaw);
        const sinY = Math.sin(f.yaw);
        // Slip stern position (where the dock edge is).
        const sternX = f.x + cosY * slip.xLocal + sinY * slip.zEdge;
        const sternZ = f.z - sinY * slip.xLocal + cosY * slip.zEdge;
        // Outward (away from dock) world direction.
        const outX = sinY * slip.side;
        const outZ = cosY * slip.side;
        // Pick a hull deterministically.
        const hullId =
          hullIds[this.spawned % hullIds.length] ?? this.playerBoatId;
        const hull = this.boatLibrary.get(hullId);
        const moored = new Boat(hull);
        // Centre the boat half its LOA out from the dock.
        const offset = hull.length * 0.5 + 0.5;
        moored.posX = sternX + outX * offset;
        moored.posZ = sternZ + outZ * offset;
        // Heading: forward = (sin h, -cos h). Set forward to the outward
        // normal so the bow points away from the dock.
        moored.heading = Math.atan2(outX, -outZ);
        this.boats.push(moored);
        this.poses.push({
          boat: moored,
          posX: moored.posX,
          posZ: moored.posZ,
          heading: moored.heading,
        });
        // Bow centre = stern + LOA along the boat's forward direction
        // (forward = outward from the dock).
        const bowX = sternX + outX * hull.length;
        const bowZ = sternZ + outZ * hull.length;
        this.slots.push({
          boat: moored,
          dockSpecId: plan.spec.id,
          side: slip.side,
          sternX,
          sternZ,
          bowX,
          bowZ,
          mooringStyle: plan.footprint.mooringStyle,
          beam: hull.beam,
          loa: hull.length,
        });
        this.spawned++;
        progressed = true;
      }
      if (!progressed) break;
    }
    if (this.spawned > 0) {
      console.log(`[Mooring] spawned ${this.spawned} moored boats`);
    }
  }

  /**
   * Resolve each slot's anchors against the freshly built docks +
   * buoys, then reserve rope-renderer slots so {@link
   * updateRopeGeometry} can stream geometry to the GPU each frame.
   */
  wireMooringLines(args: {
    annotationDocks: readonly Dock[];
    buoys: readonly Buoy[];
    ropeRenderer: MooringRopeRenderer;
  }): void {
    const { annotationDocks, buoys, ropeRenderer } = args;
    this.ropeRenderer = ropeRenderer;
    const dockById = new Map<string, Dock>();
    for (const ad of annotationDocks) dockById.set(ad.spec.id, ad);
    for (const slot of this.slots) {
      const adock = dockById.get(slot.dockSpecId);
      if (!adock) continue;
      // Project each bollard back into dock-local frame to filter by side.
      const cosD = Math.cos(adock.headingY);
      const sinD = Math.sin(adock.headingY);
      const sideBollards = adock.bollards.filter((b) => {
        const w = b.getWorldPos();
        const dx = w[0] - adock.posX;
        const dz = w[2] - adock.posZ;
        // Inverse Y-rotation (heading R(y) maps local→world; here world→local).
        const localZ = -sinD * dx + cosD * dz;
        return Math.sign(localZ) === slot.side;
      });
      if (sideBollards.length === 0) continue;
      // Sort bollards by world-space distance to the boat's stern.
      sideBollards.sort((a, b) => {
        const wa = a.getWorldPos();
        const wb = b.getWorldPos();
        const da = (wa[0] - slot.sternX) ** 2 + (wa[2] - slot.sternZ) ** 2;
        const db = (wb[0] - slot.sternX) ** 2 + (wb[2] - slot.sternZ) ** 2;
        return da - db;
      });
      const sternBodyX = -slot.loa * 0.45;
      const halfBeam = slot.beam * 0.45;
      // Stern-port → nearest bollard, stern-stbd → 2nd-nearest. If only
      // one bollard is on this side, both stern lines share it.
      const portAnchor = sideBollards[0]!;
      const stbdAnchor = sideBollards[1] ?? sideBollards[0]!;
      // restLength tuned per-line so a small visible sag remains.
      const portDist = Math.hypot(
        portAnchor.getWorldPos()[0] - slot.sternX,
        portAnchor.getWorldPos()[2] - slot.sternZ,
      );
      const stbdDist = Math.hypot(
        stbdAnchor.getWorldPos()[0] - slot.sternX,
        stbdAnchor.getWorldPos()[2] - slot.sternZ,
      );
      this.lines.push({
        boat: slot.boat,
        cleatBody: [sternBodyX, CLEAT_Y_M, -halfBeam],
        anchor: portAnchor,
        restLength:
          Math.max(MIN_REST_LENGTH_M, portDist + halfBeam) * STERN_SAG_FACTOR,
        ropeStart: -1,
      });
      this.lines.push({
        boat: slot.boat,
        cleatBody: [sternBodyX, CLEAT_Y_M, +halfBeam],
        anchor: stbdAnchor,
        restLength:
          Math.max(MIN_REST_LENGTH_M, stbdDist + halfBeam) * STERN_SAG_FACTOR,
        ropeStart: -1,
      });
      // Bow line to the nearest buoy (buoy-style docks only).
      if (slot.mooringStyle === "buoy") {
        let nearestBuoy: Buoy | undefined;
        let bestD = Infinity;
        for (const b of buoys) {
          const dx = b.posX - slot.bowX;
          const dz = b.posZ - slot.bowZ;
          const d = dx * dx + dz * dz;
          if (d < bestD) {
            bestD = d;
            nearestBuoy = b;
          }
        }
        if (nearestBuoy) {
          const bowBodyX = slot.loa * 0.45;
          const bowDist = Math.sqrt(bestD);
          this.lines.push({
            boat: slot.boat,
            cleatBody: [bowBodyX, CLEAT_Y_M, 0],
            anchor: nearestBuoy,
            restLength: Math.max(MIN_REST_LENGTH_M, bowDist) * BOW_SAG_FACTOR,
            ropeStart: -1,
          });
        }
      }
    }
    // Reserve rope-segment slots for the static moored-boat lines.
    for (const ml of this.lines) ml.ropeStart = ropeRenderer.addLine();
    if (this.lines.length > 0) {
      console.log(`[Mooring] drew ${this.lines.length} stern lines`);
    }
  }

  /**
   * Per-frame: integrate moored boats so they bob with waves, then
   * pin XZ pose + heading back to the slip pose recorded at spawn.
   * Vertical heave / pitch / roll set by buoyancy are kept.
   */
  stepAndPin(
    dt: number,
    windWx: number,
    windWz: number,
    waterField: WaterField,
    waterLevel: number,
  ): void {
    if (this.spawned === 0) return;
    for (let i = this.boats.length - this.spawned; i < this.boats.length; i++) {
      this.boats[i]!.step(dt, windWx, windWz, waterField, waterLevel);
    }
    for (const m of this.poses) {
      m.boat.posX = m.posX;
      m.boat.posZ = m.posZ;
      m.boat.heading = m.heading;
      m.boat.surge = 0;
      m.boat.sway = 0;
      m.boat.yawRate = 0;
    }
  }

  /**
   * Per-frame: refresh rope geometry from each line's cleat + anchor
   * world positions. Caller still needs to mark the rope group dirty
   * (the player stations renderer does that for the shared mesh).
   */
  updateRopeGeometry(waterLevel: number): void {
    if (this.lines.length === 0) return;
    const ropeRenderer = this.ropeRenderer;
    if (!ropeRenderer) return;
    for (const ml of this.lines) {
      const [ax, az] = ml.boat.bodyToWorld(
        ml.cleatBody[0],
        ml.cleatBody[1],
        ml.cleatBody[2],
      );
      const ay = ml.boat.posY + ml.cleatBody[1];
      const [bx, by, bz] = ml.anchor.getWorldPos();
      ropeRenderer.updateLine(
        ml.ropeStart,
        [ax, ay, az],
        [bx, by, bz],
        ml.restLength,
        waterLevel,
      );
    }
  }
}
